import React, { cloneElement, forwardRef, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'

import FLYOUT_POSITION from '../common/anchorPoints'
/**
 * @readonly
 * @enum {import('./interfaces').ArrowAlign}
 */
const ARROW_ALIGN = {
  START: 'start',
  CENTER: 'center',
  END: 'end'
}
const ARROW_ALIGN_VALUES = Object.values(ARROW_ALIGN)

/**
 * Create a React Flyout Trigger component
 * @param {import('./interfaces').FlyoutTriggerProps} props
 * @param {import('react').RefObject<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function FlyoutTriggerWrapper (
  {
    children,
    'data-position': dataPosition,
    'data-container': element,
    arrowAlign,
    onClose,
    shouldFocus,
    ...rest
  },
  ref
) {
  const isShown = !!children
  /**
   * @type {import('../common/interfaces').AnchorPoint | import('../common/interfaces').Position}
   */
  const position = dataPosition || {}

  useLayoutEffect(() => {
    if (shouldFocus && isShown) {
      ref.current.focus()
    }
  }, [shouldFocus, isShown, element])

  return (
    <div
      className='zb-flyout-container'
      ref={ref}
      hidden={!isShown}
      {...rest}
    >
      {isShown ? cloneElement(children, getFlyoutProps()) : null}
    </div>
  )

  function getFlyoutProps () {
    const alignment = normalizeArrowAlign(arrowAlign)
    const arrowDirection = getArrowDirection(position)
    const arrowPosition = element.current
      ? getArrowPosition(position, element.current, alignment)
      : 0

    return {
      isShown,
      onClose,
      arrowDirection,
      arrowPosition
    }
  }
}

/**
 * @param {import('../common/interfaces').Position} position
 * @returns {string}
 */
function getArrowDirection (position) {
  const pos = position.direction === FLYOUT_POSITION.TOP
    ? 'bottom'
    : 'top'
  const newPos = position.direction === FLYOUT_POSITION.RIGHT
    ? 'left'
    : pos
  return position.direction === FLYOUT_POSITION.LEFT
    ? 'right'
    : newPos
}

/**
 * @param {import('../common/interfaces').Position} position
 * @param {Element} element
 * @param {import('./interfaces').ArrowAlignment} arrowAlign
 * @returns {string}
 */
function getArrowPosition (position, element, arrowAlign) {
  const elementBounds = element.getBoundingClientRect()
  const { direction, align, offset } = position
  const { at: arrowAlignAt, offset: arrowAlignOffset } = arrowAlign
  const totalOffset = offset + arrowAlignOffset
  const length =
    direction === 'top' || direction === 'bottom'
      ? elementBounds.width
      : elementBounds.height

  let arrowPosition

  if (align === 'left' || align === 'top') {
    const newArrPos = arrowAlignAt === ARROW_ALIGN.END
      ? `${length - totalOffset}px`
      : length / 2 + offset + 'px'
    arrowPosition =
      arrowAlignAt === ARROW_ALIGN.START
        ? `${totalOffset}px`
        : newArrPos
  } else if (align === 'right' || align === 'bottom') {
    const newArrPos = arrowAlignAt === ARROW_ALIGN.END
      ? `calc(100% - ${totalOffset}px)`
      : `calc(100% - ${length / 2 + offset}px)`
    arrowPosition =
      arrowAlignAt === ARROW_ALIGN.START
        ? `calc(100% - ${length - totalOffset}px)`
        : newArrPos
  } else {
    const newArrPos = arrowAlignAt === ARROW_ALIGN.END
      ? `calc(50% + ${length / 2 + arrowAlignOffset}px)`
      : `calc(50% + ${arrowAlignOffset}px)`
    arrowPosition =
      arrowAlignAt === ARROW_ALIGN.START
        ? `calc(50% - ${length / 2 + arrowAlignOffset}px)`
        : newArrPos
  }

  return arrowPosition
}

/**
 * @param {import('./interfaces').ArrowAlign | import('./interfaces').ArrowAlignment} arrowAlign
 * @returns {import('./interfaces').ArrowAlignment}
 */
function normalizeArrowAlign (arrowAlign) {
  return typeof arrowAlign === 'string'
    ? arrowAlignStrToObj(arrowAlign)
    : {
      at: arrowAlign.at,
      offset: isNaN(arrowAlign.offset) ? 0 : arrowAlign.offset
    }
}

/**
 * @param {import('./interfaces').ArrowAlign} arrowAlignStr
 * @returns {import('./interfaces').ArrowAlignment}
 */
function arrowAlignStrToObj (arrowAlignStr) {
  return {
    at: arrowAlignStr,
    offset: 0
  }
}

const Wrapper = forwardRef(FlyoutTriggerWrapper)

Wrapper.displayName = 'FlyoutTriggerWrapper'
Wrapper.defaultProps = {
  position: FLYOUT_POSITION.RIGHT,
  arrowAlign: ARROW_ALIGN.CENTER
}
Wrapper.propTypes = {
  onClose: PropTypes.func,
  position: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(FLYOUT_POSITION)),
    PropTypes.shape({
      direction: PropTypes.string,
      align: PropTypes.string,
      offset: PropTypes.number
    })
  ]),
  arrowAlign: PropTypes.oneOfType([
    PropTypes.oneOf(ARROW_ALIGN_VALUES),
    PropTypes.shape({
      at: PropTypes.oneOf(ARROW_ALIGN_VALUES),
      offset: PropTypes.number
    })
  ]),
  isShown: PropTypes.bool,
  shouldFocus: PropTypes.bool
}

export default Wrapper
